export enum WSActionTypes {
  CLIENT_JOIN_WAITING_ROOM = 'join-waiting-room',
  CLIENT_EXIT = 'exit',
  CLIENT_MAKE_MOVE = 'make-fight-move',
  CLIENT_GIVE_UP = 'give-up',
  CLIENT_GET_FIGHT_DATA = 'get-fight-data',
  SERVER_WAITING_JOIN_SUCCESS = 'join-waiting-room-success',
  SERVER_WAITING_JOIN_FAILURE = 'join-waiting-room-failure',
  SERVER_WAITING_EXIT_SUCCESS = 'waiting-room-exit-success',
  SERVER_WAITING_EXIT_FAILURE = 'waiting-room-exit-failure',
  SERVER_WAITING_EXIT_MAINTENANCE = 'waiting-room-exit-mainenance',
  SERVER_FIGHT_START = 'fight-start',
  SERVER_FIGHT_END = 'fight-end',
  SERVER_FIGHT_ROUND_MAKE_MOVE_SUCCESS = 'make-fight-move-success',
  SERVER_FIGHT_ROUND_MAKE_MOVE_FAILURE = 'make-fight-move-failure',
  SERVER_FIGHT_ROUND_OPPONENT_READY = 'opponent-move-ready',
  SERVER_FIGHT_ROUND_ROUND_START = 'round-start',
  SERVER_FIGHT_ROUND_ROUND_RESULTS = 'round-results',
  SERVER_FIGHT_GIVE_UP_SUCCESS = 'give-up-success',
  SERVER_FIGHT_GIVE_UP_FAILURE = 'give-up-failure',
  SERVER_FIGHT_GET_FIGHT_DATA_SUCCESS = 'get-fight-data-success',
  SERVER_FIGHT_GET_FIGHT_DATA_FAILURE = 'get-fight-data-failure',
  SERVER_COMMON_PARSE_ERROR = 'poorly-encoded-request',
  SERVER_COMMON_UNAUTHORIZED = 'incorrect-session-id',
  SERVER_COMMON_DUPLICATED_SESSION = 'duplicated-session',
  SERVER_COMMON_UNEXPECTED_ERROR = 'unexpected-error'
}

export const wsActionTypes = {
  client: {
    joinWaitingRoom: WSActionTypes.CLIENT_JOIN_WAITING_ROOM,
    exit: WSActionTypes.CLIENT_EXIT,
    makeMove: WSActionTypes.CLIENT_MAKE_MOVE,
    giveUp: WSActionTypes.CLIENT_GIVE_UP,
    getFightData: WSActionTypes.CLIENT_GET_FIGHT_DATA
  },
  server: {
    //waiting room
    waitingRoom: {
      joinS: WSActionTypes.SERVER_WAITING_JOIN_SUCCESS,
      joinF: WSActionTypes.SERVER_WAITING_JOIN_FAILURE,
      exitS: WSActionTypes.SERVER_WAITING_EXIT_SUCCESS,
      exitF: WSActionTypes.SERVER_WAITING_EXIT_FAILURE,
      exitMaintenance: WSActionTypes.SERVER_WAITING_EXIT_MAINTENANCE
    },
    //fight
    fight: {
      start: WSActionTypes.SERVER_FIGHT_START,
      end: WSActionTypes.SERVER_FIGHT_END,
      round: {
        makeMoveS: WSActionTypes.SERVER_FIGHT_ROUND_MAKE_MOVE_SUCCESS,
        makeMoveF: WSActionTypes.SERVER_FIGHT_ROUND_MAKE_MOVE_FAILURE,
        opponentReady: WSActionTypes.SERVER_FIGHT_ROUND_OPPONENT_READY,
        roundStart: WSActionTypes.SERVER_FIGHT_ROUND_ROUND_START,
        roundResults: WSActionTypes.SERVER_FIGHT_ROUND_ROUND_RESULTS
      },
      giveUpS: WSActionTypes.SERVER_FIGHT_GIVE_UP_SUCCESS,
      giveUpF: WSActionTypes.SERVER_FIGHT_GIVE_UP_FAILURE,
      getFightDataS: WSActionTypes.SERVER_FIGHT_GET_FIGHT_DATA_SUCCESS,
      getFightDataF: WSActionTypes.SERVER_FIGHT_GET_FIGHT_DATA_FAILURE
    },
    //common
    parseError: WSActionTypes.SERVER_COMMON_PARSE_ERROR,
    unauthorized: WSActionTypes.SERVER_COMMON_UNAUTHORIZED,
    duplicatedSession: WSActionTypes.SERVER_COMMON_DUPLICATED_SESSION,
    unexpectedError: WSActionTypes.SERVER_COMMON_UNEXPECTED_ERROR
  }
};

const flat = (obj: any): any[] => {
  let result: any[] = [];
  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object') result = result.concat(flat(obj[key]));
    else result.push(obj[key]);
  });
  return result;
};

export type WSMessageStatus =
  | 200
  | 400
  | 401
  | 404
  | 422
  | 500
  | 228
  | 307
  | undefined;

export interface WSMessage<T> {
  actionType: WSActionTypes;
  token?: string;
  data: T;
  status?: WSMessageStatus;
  reqId?: string;
}

interface WSParseError {
  error: { message: string };
  id?: string;
}

const encodeWSMessage = <T>(
  message: WSMessage<T>,
  isServer: boolean
): string => {
  const { actionType, token, reqId, status, data } = message;
  if (!actionType) throw new Error('ActionType is required!');
  if (!isServer) {
    if (!token) {
      throw new Error('Token is required for authorization!');
    }
    if (!reqId) {
      throw new Error('Request ID is required for client-side requests');
    }
  }
  const body: WSMessage<T> = { actionType, data };
  if (token) body.token = token;
  if (reqId) body.reqId = reqId;
  if (status) body.status = status;
  if (data) body.data = data;
  return JSON.stringify(body);
};

const parseWebSocketMessage = <T>(
  message: string,
  isServer: boolean = false
): WSMessage<T> | WSParseError => {
  try {
    const parsed = JSON.parse(message);
    //EMPTY REQUEST
    if (!parsed) return { error: { message: 'Parse error. Empty message' } };
    //NO TOKEN or REQUESTID for client messages
    if (!isServer) {
      if (!parsed.token) {
        return { error: { message: 'No token specified' } };
      }
      if (!parsed.reqId) {
        return { error: { message: 'No Request ID specified' } };
      }
    }
    //INVALID ACTIONTYPE
    const actionsStack = flat(
      isServer ? wsActionTypes.server : wsActionTypes.client
    );
    if (!actionsStack.includes(parsed.actionType)) {
      return { error: { message: 'Invalid action!' }, id: parsed.id };
    }

    return {
      actionType: parsed.actionType,
      token: parsed.token,
      data: parsed.data || {},
      status: parsed.status,
      reqId: parsed.reqId
    };
  } catch (error: any) {
    return { error: { message: error.message } };
  }
};

export const parseWSResponse = (message: string) =>
  parseWebSocketMessage(message, true);

export const parseWSRequest = (message: string) =>
  parseWebSocketMessage(message, false);

export const encodeWSRequest = <T>(data: WSMessage<T>) =>
  encodeWSMessage(data, false);

export const encodeWSResponse = <T>(data: WSMessage<T>) =>
  encodeWSMessage(data, true);
